// Admin-anrop använder Supabase-session Bearer-token, inte x-admin-secret.
import { loadSupabase } from './loadSupabase';

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const debugNetwork = import.meta.env.DEV && import.meta.env.VITE_DEBUG_NETWORK === 'true';
const logNetwork = (...args) => {
  if (debugNetwork) console.log(...args);
};
const warnNetwork = (...args) => {
  if (debugNetwork) console.warn(...args);
};
const errorNetwork = (...args) => {
  if (debugNetwork) console.error(...args);
};

// x-admin-secret har tagits bort från frontend — admin-anrop använder Bearer via Supabase-session.
// Server adminSecretHeader finns kvar server-side i server/lib/adminAuth.js som fallback.
const adminSecretHeader = () => ({});

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error("Session timeout")), ms))]);

const getSupabaseStorageKey = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) return null;
  try {
    const { hostname } = new URL(url);
    const ref = hostname.split(".")[0];
    return ref ? `sb-${ref}-auth-token` : null;
  } catch (_) {
    return null;
  }
};

const getCachedAccessToken = () => {
  if (typeof localStorage === "undefined") return null;
  const key = getSupabaseStorageKey();
  if (!key) return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.access_token || parsed?.currentSession?.access_token || null;
  } catch (_) {
    return null;
  }
};

const buildAuthHeaders = async (url) => {
  const base = import.meta.env.VITE_API_URL || '';
  const fullUrl = url.startsWith('/api') ? `${base}${url}` : url;
  const isApiRequest = base ? fullUrl.startsWith(base) : fullUrl.startsWith('/api');
  if (!isApiRequest) return {};

  let session = null;
  try {
    const { data } = await withTimeout(loadSupabase().then((supabase) => supabase.auth.getSession()), 1500);
    session = data?.session || null;
  } catch (_) {
    session = null;
  }
  const accessToken = session?.access_token || getCachedAccessToken();
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
};

async function fetchBlob(url, { method = 'GET', headers = {}, body } = {}) {
  const base = import.meta.env.VITE_API_URL || '';
  const fullUrl = url.startsWith('/api') ? `${base}${url}` : url;
  const authHeaders = await buildAuthHeaders(url);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const isFormData =
      typeof FormData !== 'undefined' && body instanceof FormData;
    const requestHeaders = isFormData
      ? { ...authHeaders, ...headers }
      : { ...JSON_HEADERS, ...authHeaders, ...headers };
    const requestBody = isFormData ? body : body ? JSON.stringify(body) : undefined;

    const res = await fetch(fullUrl, {
      method,
      headers: requestHeaders,
      body: requestBody,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      const raw = await res.text().catch(() => '');
      let message = raw;
      try {
        const parsed = JSON.parse(raw);
        message = parsed?.error || parsed?.message || raw;
      } catch (_) {
        // not JSON, keep raw
      }
      throw new Error(message || `${res.status} ${res.statusText}`);
    }

    return await res.blob();
  } finally {
    clearTimeout(timeoutId);
  }
}

async function fetchJSON(url, { method = 'GET', headers = {}, body, timeoutMs = 30000 } = {}) {
  logNetwork('🔵 fetchJSON START:', { method, url, hasBody: !!body });
  
  const base = import.meta.env.VITE_API_URL || '';
  const fullUrl = url.startsWith('/api') ? `${base}${url}` : url;
  const isApiRequest = base ? fullUrl.startsWith(base) : fullUrl.startsWith('/api');
  
  logNetwork('🔵 fetchJSON fullUrl:', fullUrl);
  
  // Hämta auth-token från Supabase för API-requests
  logNetwork('🔵 fetchJSON: Getting session...');
  let session = null;
  if (isApiRequest) {
    try {
      const { data, error } = await withTimeout(loadSupabase().then((supabase) => supabase.auth.getSession()), 1500);
      if (error) {
        warnNetwork('🔵 fetchJSON: Session error (non-blocking):', error);
      } else if (data) {
        session = data?.session;
        logNetwork('🔵 fetchJSON: Session retrieved:', !!session);
      }
    } catch (e) {
      warnNetwork('🔵 fetchJSON: Session exception (non-blocking):', e.message || e);
    }
  }
  
  const accessToken = isApiRequest ? session?.access_token || getCachedAccessToken() : null;
  const authHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  
  logNetwork('🔵 fetchJSON: Making fetch request...', { 
    method, 
    url: fullUrl, 
    hasBody: !!body, 
    bodyType: typeof body, 
    bodyKeys: body ? Object.keys(body) : [],
    hasAuth: !!authHeaders.Authorization
  });
  
  // Lägg till timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    errorNetwork(`🔴 fetchJSON: TIMEOUT after ${timeoutMs}ms`);
    controller.abort();
  }, timeoutMs);
  
  try {
    const isFormData =
      typeof FormData !== 'undefined' && body instanceof FormData;
    const requestHeaders = isFormData
      ? { ...authHeaders, ...headers }
      : { ...JSON_HEADERS, ...authHeaders, ...headers };
    const requestBody = isFormData ? body : body ? JSON.stringify(body) : undefined;

    logNetwork('🔵 fetchJSON: Calling fetch...');
    const res = await fetch(fullUrl, {
      method,
      headers: requestHeaders,
      body: requestBody,
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    logNetwork('🔵 fetchJSON: Response received:', { status: res.status, statusText: res.statusText, ok: res.ok });
    
    if (!res.ok) {
      const raw = await res.text().catch(() => '');
      let message = raw;
      let code;
      try {
        const parsed = JSON.parse(raw);
        message = parsed?.error || parsed?.message || raw;
        code = parsed?.code;
      } catch (_) {
        // not JSON, keep raw
      }
      errorNetwork('🔴 fetchJSON error response:', {
        status: res.status,
        statusText: res.statusText,
        body: raw,
      });
      const error = new Error(message || `${res.status} ${res.statusText}`);
      error.status = res.status;
      error.code = code;
      throw error;
    }
    
    const data = await res.json();
    logNetwork('✅ fetchJSON success:', { url: fullUrl, dataKeys: Object.keys(data || {}) });
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      errorNetwork('🔴 fetchJSON timeout:', fullUrl);
      throw new Error(`Request timeout - servern svarade inte inom ${Math.round(timeoutMs / 1000)} sekunder`);
    }
    errorNetwork('🔴 fetchJSON error:', error);
    throw error;
  }
}

export const api = {
  // Public
  registerGroup: (body) => fetchJSON('/api/public/register-group', { method: 'POST', body }),
  getSellerInvite: (token) => fetchJSON(`/api/public/register-seller/${encodeURIComponent(token)}`),
  registerSeller: (token, body) => fetchJSON(`/api/public/register-seller/${encodeURIComponent(token)}`, { method: 'POST', body }),
  acceptSellerInvite: (token) =>
    fetchJSON(`/api/public/register-seller/${encodeURIComponent(token)}/accept`, { method: 'POST' }),
  requestPasswordReset: (email) =>
    fetchJSON('/api/public/auth/reset-password', { method: 'POST', body: { email } }),
  getMyGroupMemberships: () => fetchJSON('/api/public/me/group-memberships'),

  // Admin
  getAdminGroupDashboard: () => fetchJSON('/api/admin/vw_group_dashboard'),
  getAdminSellerStats: () => fetchJSON('/api/admin/vw_seller_stats'),
  createAdminGroup: (body) => fetchJSON('/api/admin/group', { method: 'POST', body }),
  createAdminGroupSuper: (body) => fetchJSON('/api/admin/groups/create', { method: 'POST', body }),
  getAdminInvoices: (groupId) => fetchJSON(groupId ? `/api/admin/invoices?group_id=${encodeURIComponent(groupId)}` : '/api/admin/invoices'),
  createAdminInvoice: (body) => fetchJSON('/api/admin/invoices', { method: 'POST', body }),
  getAdminProducts: ({ search, active } = {}) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (typeof active === 'boolean') params.set('active', active ? 'true' : 'false');
    const qs = params.toString();
    return fetchJSON(`/api/admin/products${qs ? `?${qs}` : ''}`, {
      headers: adminSecretHeader(),
    });
  },


  previewAdminVariantImageRemap: (productId, payload) =>
    fetchJSON(`/api/admin/products/variant-image-remap/${encodeURIComponent(productId)}/preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...adminSecretHeader(),
      },
      body: JSON.stringify(payload),
    }),
  getAdminVariantImageReviewQueue: async () =>
    fetchJSON('/api/admin/products/variant-image-review-queue', {
      headers: adminSecretHeader(),
    }),
  getAdminProduct: (id) =>
    fetchJSON(`/api/admin/products/${encodeURIComponent(id)}`, {
      headers: adminSecretHeader(),
    }),
  importAdminProducts: async ({ file, products } = {}) => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      return fetchJSON('/api/admin/products/import', {
        method: 'POST',
        body: formData,
        headers: adminSecretHeader(),
      });
    }
    if (Array.isArray(products) && products.length) {
      return fetchJSON('/api/admin/products/import', {
        method: 'POST',
        body: { products },
        headers: adminSecretHeader(),
      });
    }
    throw new Error('Ange antingen file eller products-array för import');
  },
  importAdminCatalogZip: ({ file } = {}) => {
    if (!file) throw new Error('Ange en ZIP-fil');
    const formData = new FormData();
    formData.append('file', file);
    return fetchJSON('/api/admin/catalog/import', {
      method: 'POST',
      body: formData,
      headers: adminSecretHeader(),
      timeoutMs: 300000,
    });
  },
  updateAdminProduct: (id, payload) =>
    fetchJSON(`/api/admin/products/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: payload,
      headers: adminSecretHeader(),
    }),
  patchAdminProduct: (id, payload) =>
    fetchJSON(`/api/admin/products/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: payload,
      headers: adminSecretHeader(),
    }),
  uploadAdminProductImage: (file, { productId } = {}) => {
    if (!file) throw new Error('Ingen fil angiven');
    const formData = new FormData();
    formData.append('file', file);
    if (productId) formData.append('productId', productId);
    return fetchJSON('/api/admin/products/upload-image', {
      method: 'POST',
      body: formData,
      headers: adminSecretHeader(),
    });
  },
  uploadAdminProductImages: (productId, files, { variantId } = {}) => {
    if (!productId) throw new Error('productId krävs');
    if (!files || !files.length) throw new Error('Ingen fil angiven');
    const formData = new FormData();
    [...files].forEach((file) => formData.append('files', file));
    if (variantId) formData.append('variantId', variantId);
    return fetchJSON(`/api/admin/products/${encodeURIComponent(productId)}/images`, {
      method: 'POST',
      body: formData,
      headers: adminSecretHeader(),
    });
  },
  deleteAdminProductImage: (imageId) => {
    if (!imageId) throw new Error('imageId krävs');
    return fetchJSON(`/api/admin/products/images/${encodeURIComponent(imageId)}`, {
      method: 'DELETE',
      headers: adminSecretHeader(),
    });
  },
  setAdminProductImagePrimary: (imageId) => {
    if (!imageId) throw new Error('imageId krävs');
    return fetchJSON(`/api/admin/products/images/${encodeURIComponent(imageId)}/primary`, {
      method: 'PATCH',
      headers: adminSecretHeader(),
    });
  },
  createAdminProductVariant: (productId, payload) => {
    if (!productId) throw new Error('productId krävs');
    return fetchJSON(`/api/admin/products/${encodeURIComponent(productId)}/variants`, {
      method: 'POST',
      headers: adminSecretHeader(),
      body: payload,
    });
  },
  updateAdminProductVariant: (variantId, payload) => {
    if (!variantId) throw new Error('variantId krävs');
    return fetchJSON(`/api/admin/products/variants/${encodeURIComponent(variantId)}`, {
      method: 'PATCH',
      headers: adminSecretHeader(),
      body: payload,
    });
  },
  deleteAdminProductVariant: (variantId) => {
    if (!variantId) throw new Error('variantId krävs');
    return fetchJSON(`/api/admin/products/variants/${encodeURIComponent(variantId)}`, {
      method: 'DELETE',
      headers: adminSecretHeader(),
    });
  },
  createAdminProduct: (payload) =>
    fetchJSON('/api/admin/products', {
      method: 'POST',
      body: payload,
      headers: adminSecretHeader(),
    }),

  // Payouts (admin)
  previewPayout: (body) => fetchJSON('/api/admin/payouts/preview', { method: 'POST', body }),
  schedulePayout: (body) => fetchJSON('/api/admin/payouts/schedule', { method: 'POST', body }),
  listPayouts: (groupId) => fetchJSON(groupId ? `/api/admin/payouts?group_id=${encodeURIComponent(groupId)}` : '/api/admin/payouts'),
  markPayoutPaid: (id) => fetchJSON(`/api/admin/payouts/${encodeURIComponent(id)}/status`, { method: 'PATCH', body: { status: 'paid' } }),
  
  // Nya payout endpoints (gruppnivå)
  getAdminPayoutsGroups: () => fetchJSON('/api/admin/payouts/groups'),
  getAdminPayoutGroup: (groupId) => fetchJSON(`/api/admin/payouts/${encodeURIComponent(groupId)}`),
  generateAdminPayout: (groupId, body) => fetchJSON(`/api/admin/payouts/${encodeURIComponent(groupId)}/generate`, { method: 'POST', body }),
  markAdminPayoutPaid: (payoutId, body) => fetchJSON(`/api/admin/payouts/${encodeURIComponent(payoutId)}/mark-paid`, { method: 'POST', body }),
  getAdminReferralClicks: () => fetchJSON('/api/admin/referral-clicks'),
  getAdminOpsToday: () => fetchJSON('/api/admin/ops/today'),
  getAdminIntegrationsHealth: () => fetchJSON('/api/admin/integrations/health'),

  // Leader
  getLeaderGroupDashboard: (groupId) =>
    fetchJSON(
      groupId
        ? `/api/leader/vw_group_dashboard?group_id=${encodeURIComponent(groupId)}`
        : '/api/leader/vw_group_dashboard'
    ),
  getLeaderSellerStats: ({ userId, salesPeriodId } = {}) => {
    const headers = {};
    if (userId) headers['X-User-Id'] = userId;
    const params = new URLSearchParams();
    if (salesPeriodId) params.set('sales_period_id', salesPeriodId);
    const qs = params.toString();
    return fetchJSON(`/api/leader/vw_seller_stats${qs ? `?${qs}` : ''}`, { headers });
  },
  getLeaderGroupMembers: (groupId) => fetchJSON(`/api/leader/groups/${encodeURIComponent(groupId)}/members`),
  getLeaderSalesPeriods: async (groupId, { userId } = {}) => {
    const headers = {};
    if (userId) headers['X-User-Id'] = userId;
    try {
      return await fetchJSON(`/api/leader/groups/${encodeURIComponent(groupId)}/sales-periods`, { headers });
    } catch (error) {
      const message = String(error?.message || '');
      if (!message.includes('Not found') && !message.includes('404')) throw error;
      const active = await fetchJSON(`/api/groups/${encodeURIComponent(groupId)}/sales-period/active`);
      return active?.sales_period ? [active.sales_period] : [];
    }
  },
  inviteLeaderSeller: (groupId, body) => fetchJSON(`/api/leader/groups/${encodeURIComponent(groupId)}/invite-seller`, { method: 'POST', body }),
  getLeaderSellerLinks: (groupId) => fetchJSON(`/api/leader/groups/${encodeURIComponent(groupId)}/seller-links`),
  createLeaderSellerLink: (body) => fetchJSON('/api/leader/seller-links', { method: 'POST', body }),
  sendLeaderSellerLink: (linkId, body) => fetchJSON(`/api/leader/seller-links/${encodeURIComponent(linkId)}/send`, { method: 'POST', body }),
  
  // Onboarding
  getOnboardingStatus: (groupId, { userId } = {}) => {
    const headers = {};
    if (userId) headers['X-User-Id'] = userId;
    return fetchJSON(`/api/leader/groups/${encodeURIComponent(groupId)}/onboarding-status`, { headers });
  },
  saveOnboardingAddress: (groupId, body) =>
    fetchJSON(`/api/leader/groups/${encodeURIComponent(groupId)}/onboarding/address`, {
      method: 'PATCH',
      body,
    }),
  saveOnboardingCampaign: (groupId, body) =>
    fetchJSON(`/api/leader/groups/${encodeURIComponent(groupId)}/onboarding/campaign`, {
      method: 'PATCH',
      body,
    }),
  completeOnboardingStep: (groupId, step, { userId } = {}) => {
    const headers = {};
    if (userId) headers['X-User-Id'] = userId;
    return fetchJSON(`/api/leader/groups/${encodeURIComponent(groupId)}/onboarding/complete-step`, {
      method: 'POST',
      headers,
      body: { step },
    });
  },
  getLeaderSellerInvites: (groupId) =>
    fetchJSON(`/api/leader/groups/${encodeURIComponent(groupId)}/seller-invites`),
  resendLeaderSellerInvite: (groupId, inviteId) =>
    fetchJSON(
      `/api/leader/groups/${encodeURIComponent(groupId)}/seller-invites/${encodeURIComponent(inviteId)}/resend`,
      { method: 'POST' }
    ),
  getLeaderSellerLinkQrCode: (groupId, linkId, { userId } = {}) => {
    const headers = {};
    if (userId) headers['X-User-Id'] = userId;
    return fetchJSON(`/api/leader/groups/${encodeURIComponent(groupId)}/seller-links/${encodeURIComponent(linkId)}/qr-code`, { headers });
  },
  getLeaderGroupPayouts: (groupId, { userId, salesPeriodId } = {}) => {
    const headers = {};
    if (userId) headers['X-User-Id'] = userId;
    const params = new URLSearchParams();
    if (salesPeriodId) params.set('sales_period_id', salesPeriodId);
    const qs = params.toString();
    const path = `/api/leader/groups/${encodeURIComponent(groupId)}/payouts${qs ? `?${qs}` : ''}`;
    return fetchJSON(path, { headers });
  },
  
  // Group endpoints
  updateGroup: ({ userId }, body) => fetchJSON('/api/group/update', { method: 'POST', headers: { 'X-User-Id': userId }, body }),
  getGroupPublicPage: (groupId) => fetchJSON(`/api/group/${encodeURIComponent(groupId)}/public-page`),
  getGroupSellers: (groupId, { userId }) => fetchJSON(`/api/group/${encodeURIComponent(groupId)}/sellers`, { headers: { 'X-User-Id': userId } }),
  
  // Marketing endpoints
  generateMarketingAssets: ({ userId }, body) => fetchJSON('/api/marketing/generate', { method: 'POST', headers: { 'X-User-Id': userId }, body }),
  
  // Notifications endpoints
  sendReminders: ({ userId }, body) => fetchJSON('/api/notifications/send-reminders', { method: 'POST', headers: { 'X-User-Id': userId }, body }),

  // Seller
  getSellerMe: ({ userId, email }) => {
    const headers = {};
    if (userId) headers['X-User-Id'] = userId;
    if (email) headers['X-User-Email'] = email;
    return fetchJSON('/api/seller/me', { headers });
  },
  getSellerMyLink: ({ userId }) => fetchJSON('/api/seller/my-link', { headers: { 'X-User-Id': userId } }),
  getSellerQrCode: ({ userId }) => fetchJSON('/api/seller/qr-code', { headers: { 'X-User-Id': userId } }),
  downloadSellerInfoPdf: ({ userId }) =>
    fetchBlob('/api/seller/seller-info.pdf', { headers: { 'X-User-Id': userId } }),
  getSellerSalesPeriods: ({ userId }) => fetchJSON('/api/seller/sales-periods', { headers: { 'X-User-Id': userId } }),
  getSellerOrders: ({ userId, salesPeriodId } = {}) => {
    const params = new URLSearchParams();
    if (salesPeriodId) params.set('sales_period_id', salesPeriodId);
    const qs = params.toString();
    return fetchJSON(`/api/seller/orders${qs ? `?${qs}` : ''}`, { headers: { 'X-User-Id': userId } });
  },
  getSellerGroupKpis: ({ userId, salesPeriodId } = {}) => {
    const params = new URLSearchParams();
    if (salesPeriodId) params.set('sales_period_id', salesPeriodId);
    const qs = params.toString();
    return fetchJSON(`/api/seller/group-kpis${qs ? `?${qs}` : ''}`, { headers: { 'X-User-Id': userId } });
  },
  getSellerProgress: (sellerId, { salesPeriodId } = {}) => {
    const params = new URLSearchParams();
    if (salesPeriodId) params.set('sales_period_id', salesPeriodId);
    const qs = params.toString();
    return fetchJSON(`/api/seller/${encodeURIComponent(sellerId)}/progress${qs ? `?${qs}` : ''}`);
  },
  sendSellerLink: ({ userId }, body) => fetchJSON('/api/seller/send-link', { method: 'POST', headers: { 'X-User-Id': userId }, body }),
  // New seller profile endpoints
  updateSellerProfile: ({ userId }, body) => fetchJSON('/api/seller/profile/update', { method: 'POST', headers: { 'X-User-Id': userId }, body }),
  updateSellerStory: ({ userId }, body) => fetchJSON('/api/seller/story/update', { method: 'POST', headers: { 'X-User-Id': userId }, body }),
  getSellerPublicPage: (sellerId) => fetchJSON(`/api/seller/${encodeURIComponent(sellerId)}/public-page`),
  getSellerShareAssets: (sellerId) => fetchJSON(`/api/seller/${encodeURIComponent(sellerId)}/share-assets`),
  getGroupSalesStats: (groupId, { salesPeriodId } = {}) => {
    const params = new URLSearchParams();
    if (salesPeriodId) params.set('sales_period_id', salesPeriodId);
    const qs = params.toString();
    return fetchJSON(`/api/groups/${encodeURIComponent(groupId)}/sales-stats${qs ? `?${qs}` : ''}`);
  },
  getLeaderSellerOrders: ({ leaderId, sellerId, salesPeriodId } = {}) => {
    const params = new URLSearchParams();
    if (salesPeriodId) params.set('sales_period_id', salesPeriodId);
    const qs = params.toString();
    return fetchJSON(`/api/leader/sellers/${encodeURIComponent(sellerId)}/orders${qs ? `?${qs}` : ''}`, {
      headers: { 'X-User-Id': leaderId },
    });
  },
  // Association (föreningschef)
  getAssociationOverview: ({ userId }) =>
    fetchJSON('/api/association/overview', {
      headers: { 'X-User-Id': userId },
    }),
  getAssociationStatsOverview: (orgId, { userId }) =>
    fetchJSON(`/api/association/${encodeURIComponent(orgId)}/stats/overview`, {
      headers: { 'X-User-Id': userId },
    }),
  getAssociationStatsTeams: (orgId, { userId }) =>
    fetchJSON(`/api/association/${encodeURIComponent(orgId)}/stats/teams`, {
      headers: { 'X-User-Id': userId },
    }),
  getAssociationStatsTopProducts: (orgId, { userId }) =>
    fetchJSON(`/api/association/${encodeURIComponent(orgId)}/stats/top-products`, {
      headers: { 'X-User-Id': userId },
    }),
  getAssociationStatsTopSellers: (orgId, { userId }) =>
    fetchJSON(`/api/association/${encodeURIComponent(orgId)}/stats/top-sellers`, {
      headers: { 'X-User-Id': userId },
    }),
  getAssociationStatsTimeseries: (orgId, days, { userId }) => {
    const params = new URLSearchParams({ days: days.toString() });
    return fetchJSON(`/api/association/${encodeURIComponent(orgId)}/stats/timeseries?${params.toString()}`, {
      headers: { 'X-User-Id': userId },
    });
  },
  
  // Admin Support
  searchSupport: (query) => {
    const params = new URLSearchParams({ q: query });
    return fetchJSON(`/api/admin/support/search?${params.toString()}`, {
      headers: adminSecretHeader(),
    });
  },
  resendOrderConfirmation: (orderId) =>
    fetchJSON(`/api/admin/support/orders/${encodeURIComponent(orderId)}/resend-confirmation`, {
      method: 'POST',
      headers: adminSecretHeader(),
    }),
  markOrderDelivered: (orderId, delivered_at) =>
    fetchJSON(`/api/admin/support/orders/${encodeURIComponent(orderId)}/mark-delivered`, {
      method: 'POST',
      headers: adminSecretHeader(),
      body: { delivered_at },
    }),
  refundOrder: (orderId, reason) =>
    fetchJSON(`/api/admin/support/orders/${encodeURIComponent(orderId)}/refund`, {
      method: 'POST',
      headers: adminSecretHeader(),
      body: { reason },
    }),
  listAdminGroups: ({ search = '' } = {}) => {
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    const qs = params.toString();
    return fetchJSON(`/api/admin/groups${qs ? `?${qs}` : ''}`, {
      headers: adminSecretHeader(),
    });
  },
  getAdminGroup: (groupId) =>
    fetchJSON(`/api/admin/groups/${encodeURIComponent(groupId)}`, {
      headers: adminSecretHeader(),
    }),
  getAdminGroupOrders: (groupId, { salesPeriodId, sellerProfileId, limit, offset } = {}) => {
    const params = new URLSearchParams();
    if (salesPeriodId) params.set('sales_period_id', salesPeriodId);
    if (sellerProfileId) params.set('seller_profile_id', sellerProfileId);
    if (limit) params.set('limit', limit.toString());
    if (offset) params.set('offset', offset.toString());
    const qs = params.toString();
    return fetchJSON(
      `/api/admin/groups/${encodeURIComponent(groupId)}/orders${qs ? `?${qs}` : ''}`,
      {
        headers: adminSecretHeader(),
      }
    );
  },
  getAdminGroupSellers: (groupId, { salesPeriodId } = {}) => {
    const params = new URLSearchParams();
    if (salesPeriodId) params.set('sales_period_id', salesPeriodId);
    const qs = params.toString();
    return fetchJSON(
      `/api/admin/groups/${encodeURIComponent(groupId)}/sellers${qs ? `?${qs}` : ''}`,
      {
        headers: adminSecretHeader(),
      }
    );
  },
  getAdminTopGroups: () =>
    fetchJSON('/api/admin/top-groups', {
      headers: adminSecretHeader(),
    }),

  listAdminGroupsOverview: ({
    bucket,
    search,
    sort,
    dir,
    limit,
    offset,
  } = {}) => {
    const params = new URLSearchParams();
    if (bucket) params.set('bucket', bucket);
    if (search) params.set('search', search);
    if (sort) params.set('sort', sort);
    if (dir) params.set('dir', dir);
    if (limit) params.set('limit', limit.toString());
    if (offset) params.set('offset', offset.toString());
    const qs = params.toString();
    return fetchJSON(`/api/admin/groups/overview${qs ? `?${qs}` : ''}`, {
      headers: adminSecretHeader(),
    });
  },
  getAdminGroupsList: () =>
    fetchJSON('/api/admin/groups', {
      headers: adminSecretHeader(),
    }),
  getAdminGroupSalesPeriods: (groupId) =>
    fetchJSON(`/api/admin/groups/${encodeURIComponent(groupId)}/sales-periods`, {
      headers: adminSecretHeader(),
    }),
  getAdminGroupSalesPeriodSummary: (groupId, periodId) =>
    fetchJSON(
      `/api/admin/groups/${encodeURIComponent(groupId)}/sales-periods/${encodeURIComponent(periodId)}/summary`,
      {
        headers: adminSecretHeader(),
      }
    ),
  createAdminGroupSalesPeriod: (groupId, body) =>
    fetchJSON(`/api/admin/groups/${encodeURIComponent(groupId)}/sales-periods`, {
      method: 'POST',
      headers: adminSecretHeader(),
      body,
    }),
  updateAdminSalesPeriod: (periodId, body) =>
    fetchJSON(`/api/admin/sales-periods/${encodeURIComponent(periodId)}`, {
      method: 'PATCH',
      headers: adminSecretHeader(),
      body,
    }),
  closeAdminSalesPeriod: (periodId) =>
    fetchJSON(`/api/admin/sales-periods/${encodeURIComponent(periodId)}/close`, {
      method: 'POST',
      headers: adminSecretHeader(),
    }),

  // Orders – statusuppdatering (plockad/skickad)
  updateOrderStatus: (orderId, status) =>
    fetchJSON(`/api/admin/orders/${encodeURIComponent(orderId)}/status`, {
      method: 'PATCH',
      headers: adminSecretHeader(),
      body: { status },
    }),

  // Admin – orders & picklists
  getAdminOrders: ({ status, delivery_mode, class_id, campaign_id, only_pickable } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (delivery_mode) params.set('delivery_mode', delivery_mode);
    if (class_id) params.set('class_id', class_id);
    if (campaign_id) params.set('campaign_id', campaign_id);
    if (only_pickable) params.set('only_pickable', 'true');
    const qs = params.toString();
    return fetchJSON(`/api/admin/orders${qs ? `?${qs}` : ''}`, {
      headers: adminSecretHeader(),
    });
  },
  getAdminOrderPicklist: (orderId) =>
    fetchJSON(`/api/admin/orders/${encodeURIComponent(orderId)}/picklist`, {
      headers: adminSecretHeader(),
    }),
  updateOrderPickAll: (orderId, picked = true) =>
    fetchJSON(`/api/admin/orders/${encodeURIComponent(orderId)}/pick-all`, {
      method: 'POST',
      headers: adminSecretHeader(),
      body: { picked },
    }),
  updateOrderStatus: (orderId, data) =>
    fetchJSON(`/api/admin/orders/${encodeURIComponent(orderId)}/status`, {
      method: 'PATCH',
      headers: adminSecretHeader(),
      body: data,
    }),
  getAdminDirectUnpackedPicklists: () =>
    fetchJSON('/api/admin/picklist/direct-unpacked', {
      headers: adminSecretHeader(),
    }),
  getAdminCollectivePicklists: (groupId, salesPeriodId) => {
    const params = new URLSearchParams();
    params.set('group_id', groupId);
    params.set('sales_period_id', salesPeriodId);
    return fetchJSON(`/api/admin/picklists/collective?${params.toString()}`, {
      headers: adminSecretHeader(),
    });
  },
  getAdminPicklistsDirect: () =>
    fetchJSON('/api/admin/picklists/direct', {
      headers: adminSecretHeader(),
    }),
  getAdminPicklistsPackedUndelivered: () =>
    fetchJSON('/api/admin/picklists/packed-undelivered', {
      headers: adminSecretHeader(),
    }),
  downloadAdminDirectPicklistPdf: (orderIds = []) => {
    const params = new URLSearchParams();
    if (orderIds.length) params.set('orderIds', orderIds.join(','));
    const qs = params.toString();
    return fetchBlob(`/api/admin/picklists/direct/print.pdf${qs ? `?${qs}` : ''}`, {
      headers: adminSecretHeader(),
    });
  },
  downloadAdminCollectivePicklistPdf: (groupId, salesPeriodId) => {
    const params = new URLSearchParams();
    params.set('group_id', groupId);
    params.set('sales_period_id', salesPeriodId);
    return fetchBlob(`/api/admin/picklists/collective/print.pdf?${params.toString()}`, {
      headers: adminSecretHeader(),
    });
  },
  getAdminPackBatch: (batchId) =>
    fetchJSON(`/api/admin/pack-batches/${encodeURIComponent(batchId)}`, {
      headers: adminSecretHeader(),
    }),
  getAdminPackBatchByPeriod: (groupId, salesPeriodId) => {
    if (!groupId || !salesPeriodId) throw new Error('groupId och salesPeriodId krävs');
    const params = new URLSearchParams();
    params.set('groupId', groupId);
    params.set('salesPeriodId', salesPeriodId);
    return fetchJSON(`/api/admin/pack-batches?${params.toString()}`, {
      headers: adminSecretHeader(),
    });
  },
  downloadAdminPackBatchPdf: (batchId) =>
    fetchBlob(`/api/admin/pack-batches/${encodeURIComponent(batchId)}/print.pdf`, {
      headers: adminSecretHeader(),
    }),
  updateOrderItemPick: (orderId, orderItemId, picked_qty) =>
    fetchJSON(
      `/api/admin/orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(orderItemId)}/pick`,
      {
        method: 'PATCH',
        headers: adminSecretHeader(),
        body: { picked_qty },
      }
    ),
  unpackOrder: (orderId) =>
    fetchJSON(`/api/admin/orders/${encodeURIComponent(orderId)}/unpack`, {
      headers: adminSecretHeader(),
      method: 'POST',
    }),

  // Checkout
  checkoutSupport: (sellerSlug) => {
    const base = import.meta.env.VITE_API_URL || '';
    return fetchJSON(`${base}/api/checkout/support/${encodeURIComponent(sellerSlug)}`);
  },

  // Sales periods
  getActiveSalesPeriod: (groupId) => {
    if (!groupId) throw new Error('groupId krävs');
    return fetchJSON(`/api/groups/${encodeURIComponent(groupId)}/sales-period/active`);
  },
  getOpenSalesPeriod: (groupId) => {
    if (!groupId) throw new Error('groupId krävs');
    return fetchJSON(`/api/admin/groups/${encodeURIComponent(groupId)}/sales-periods/open`, {
      headers: adminSecretHeader(),
    });
  },
  closeSalesPeriod: (periodId) => {
    if (!periodId) throw new Error('salesPeriodId krävs');
    return fetchJSON(`/api/sales-periods/${encodeURIComponent(periodId)}/close`, { method: 'POST' });
  },
  closeActiveSalesPeriod: (groupId) => {
    if (!groupId) throw new Error('groupId krävs');
    return fetchJSON(`/api/groups/${encodeURIComponent(groupId)}/sales-period/close`, { method: 'POST' });
  },
  createGroupSalesPeriod: (groupId, body) => {
    if (!groupId) throw new Error('groupId krävs');
    return fetchJSON(`/api/groups/${encodeURIComponent(groupId)}/sales-periods`, {
      method: 'POST',
      body,
    });
  },

  // Fortnox
  getFortnoxStatus: () =>
    fetchJSON('/api/fortnox/status', {
      headers: adminSecretHeader(),
    }),
  startFortnoxAuth: () =>
    fetchJSON('/api/fortnox/auth/start', {
      method: 'POST',
      headers: adminSecretHeader(),
    }),
  completeFortnoxAuth: (body) =>
    fetchJSON('/api/fortnox/auth/callback', {
      method: 'POST',
      headers: adminSecretHeader(),
      body,
    }),
  disconnectFortnox: () =>
    fetchJSON('/api/fortnox/disconnect', {
      method: 'POST',
      headers: adminSecretHeader(),
    }),
  getFortnoxGroupLink: (groupId) => {
    if (!groupId) throw new Error('groupId krävs');
    return fetchJSON(`/api/fortnox/groups/${encodeURIComponent(groupId)}/link`, {
      headers: adminSecretHeader(),
    });
  },
  syncFortnoxCustomer: (groupId) => {
    if (!groupId) throw new Error('groupId krävs');
    return fetchJSON(`/api/fortnox/groups/${encodeURIComponent(groupId)}/sync-customer`, {
      method: 'POST',
      headers: adminSecretHeader(),
    });
  },
  getFortnoxInvoiceLink: (salesPeriodId) => {
    if (!salesPeriodId) throw new Error('salesPeriodId krävs');
    return fetchJSON(`/api/fortnox/sales-periods/${encodeURIComponent(salesPeriodId)}/invoice`, {
      headers: adminSecretHeader(),
    });
  },
  previewFortnoxInvoice: (salesPeriodId) => {
    if (!salesPeriodId) throw new Error('salesPeriodId krävs');
    return fetchJSON(
      `/api/fortnox/sales-periods/${encodeURIComponent(salesPeriodId)}/preview-invoice`,
      {
        headers: adminSecretHeader(),
      }
    );
  },
  syncFortnoxInvoiceStatus: (salesPeriodId) => {
    if (!salesPeriodId) throw new Error('salesPeriodId krävs');
    return fetchJSON(
      `/api/fortnox/sales-periods/${encodeURIComponent(salesPeriodId)}/sync-invoice`,
      {
        method: 'POST',
        headers: adminSecretHeader(),
      }
    );
  },
  listFortnoxInvoicesOverview: () =>
    fetchJSON('/api/fortnox/invoices/overview', {
      headers: adminSecretHeader(),
    }),
  syncAllFortnoxInvoiceStatuses: () =>
    fetchJSON('/api/fortnox/invoices/sync-status', {
      method: 'POST',
      headers: adminSecretHeader(),
    }),
  createFortnoxInvoice: (salesPeriodId) => {
    if (!salesPeriodId) throw new Error('salesPeriodId krävs');
    return fetchJSON(`/api/fortnox/sales-periods/${encodeURIComponent(salesPeriodId)}/create-invoice`, {
      method: 'POST',
      headers: adminSecretHeader(),
    });
  },

  // Publikt stödflöde – grupper & säljare
  searchGroups: (query = '') => {
    const params = new URLSearchParams();
    if (query.trim()) params.set('query', query.trim());
    const qs = params.toString();
    return fetchJSON(`/api/public/groups${qs ? `?${qs}` : ''}`);
  },
  getGroupSellers: (groupId) => {
    if (!groupId) throw new Error('groupId krävs');
    return fetchJSON(`/api/public/groups/${encodeURIComponent(groupId)}/sellers`);
  },

  // Admin - Organizations
  listAdminOrganizations: () => fetchJSON('/api/admin/organizations', { headers: adminSecretHeader() }),
  getAdminOrganization: (organizationId) =>
    fetchJSON(`/api/admin/organizations/${encodeURIComponent(organizationId)}`, {
      headers: adminSecretHeader(),
    }),
  createAdminOrganization: (body) => fetchJSON('/api/admin/organizations/create', { method: 'POST', headers: adminSecretHeader(), body }),
  updateAdminOrganization: (organizationId, body) =>
    fetchJSON(`/api/admin/organizations/${encodeURIComponent(organizationId)}`, {
      method: 'PATCH',
      headers: adminSecretHeader(),
      body,
    }),
  listAdminSalesPeriods: (groupId) => {
    if (!groupId) throw new Error('groupId krävs');
    return fetchJSON(`/api/admin/groups/${encodeURIComponent(groupId)}/sales-periods`, {
      headers: adminSecretHeader(),
    });
  },

  // Admin - Groups (new endpoint)
  createAdminGroupNew: (body) => fetchJSON('/api/admin/groups/create-new', { method: 'POST', headers: adminSecretHeader(), body }),
  getAdminGroupForEdit: (groupId) => fetchJSON(`/api/admin/groups/${encodeURIComponent(groupId)}/edit`, { headers: adminSecretHeader() }),
  updateAdminGroup: (groupId, body) => fetchJSON(`/api/admin/groups/${encodeURIComponent(groupId)}`, { method: 'PATCH', headers: adminSecretHeader(), body }),

  // Admin - Group approval flow
  getPendingGroups: () => fetchJSON('/api/admin/groups/pending', { headers: adminSecretHeader() }),
  updateGroupStatus: (groupId, status, rejectionReason) => 
    fetchJSON(`/api/admin/groups/${encodeURIComponent(groupId)}/status`, {
      method: 'PATCH',
      headers: adminSecretHeader(),
      body: { status, rejection_reason: rejectionReason || null },
    }),

  // Admin - Audit log
  getGroupAuditLog: (groupId) => 
    fetchJSON(`/api/admin/groups/${encodeURIComponent(groupId)}/audit-log`, {
      headers: adminSecretHeader(),
    }),
};

// Minimal additions for direct helpers used by pages
// Added: named helpers to match requested API surface
export function getSupportBySlug(sellerSlug) {
  const base = import.meta.env.VITE_API_URL || '';
  return fetchJSON(`${base}/api/checkout/support/${encodeURIComponent(sellerSlug)}`);
}

export function createCheckout(payload) {
  const base = import.meta.env.VITE_API_URL || '';
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid checkout payload');
  }
  
  return fetchJSON(`${base}/api/checkout`, {
    method: 'POST',
    body: payload,
  });
}
